import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { searchOnlineDoctors } from '../../services/api/pool-manager';
import { debounce } from '../../utils/debounce';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const SearchInput = styled.input`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 8px 12px;
  margin-top: 8px;
  border: 1px solid #d5d5d5;
  border-radius: 0.375rem;
  font-size: 0.9rem;
  max-width: 30rem;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  max-height: 22rem;
  overflow-y: auto;
`;

const DoctorRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  margin-top: 6px;
  background-color: ${(props) => (props.selected ? '#f0e9fa' : '#fafafa')};
  border: 0.1px #cecece solid;
  border-radius: 3px;
  cursor: pointer;
  :hover {
    background-color: #f0f0f0;
  }
`;

const DoctorMeta = styled.div`
  display: flex;
  flex-direction: column;
`;

const HelperText = styled(RegularText)`
  color: #888;
  margin-top: 10px;
`;

const MIN_QUERY_LENGTH = 2;

class DoctorSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      doctors: [],
      loading: false,
      searched: false,
    };
    // Debounce the actual network call (500ms), mirroring existing search delay.
    this.debouncedSearch = debounce(this.runSearch, 500);
  }

  runSearch = (term) => {
    const trimmed = (term || '').trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      this.setState({ doctors: [], loading: false, searched: false });
      return;
    }
    this.setState({ loading: true });
    searchOnlineDoctors(trimmed.toLowerCase())
      .then((res) => {
        if (res && res.message === 'success') {
          this.setState({ doctors: res.doctors || [], searched: true });
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to search doctors');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to search doctors');
        this.setState({ doctors: [], searched: true });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onChange = (e) => {
    const query = e.target.value;
    this.setState({ query });
    this.debouncedSearch(query);
  };

  render() {
    const { query, doctors, loading, searched } = this.state;
    const { selectedDoctorId, onSelectDoctor } = this.props;

    return (
      <Container>
        <BoldText>Find a doctor</BoldText>
        <SearchInput
          value={query}
          placeholder="Search by name or doctor ID"
          onChange={this.onChange}
        />
        {loading ? (
          <LoadingComponent />
        ) : (
          <ResultsContainer>
            {doctors.map((doctor) => (
              <DoctorRow
                key={doctor.doctorId}
                selected={
                  selectedDoctorId &&
                  `${selectedDoctorId}` === `${doctor.doctorId}`
                }
                onClick={() => onSelectDoctor(doctor)}
              >
                <DoctorMeta>
                  <BoldText>{doctor.doctorName}</BoldText>
                  <RegularText color="#666">
                    {`ID: ${doctor.doctorId}`}
                    {doctor.centerName ? ` · ${doctor.centerName}` : ''}
                  </RegularText>
                </DoctorMeta>
              </DoctorRow>
            ))}
            {searched && !doctors.length ? (
              <HelperText>No doctors found for "{query}".</HelperText>
            ) : null}
            {!searched && query.trim().length < MIN_QUERY_LENGTH ? (
              <HelperText>
                Type at least {MIN_QUERY_LENGTH} characters to search.
              </HelperText>
            ) : null}
          </ResultsContainer>
        )}
      </Container>
    );
  }
}

export default DoctorSearch;
